import React from 'react';
import styled, { css } from 'styled-components';

import { useTranslation } from 'react-i18next';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { Typography, Fade } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { PopperPropsWithDataTestId } from '@mytypes';
import { PALETTE, Z_INDEX, BUTTON, FONT_SIZES } from '~/assets/styles/base';

const Wrapper = styled.div``;

type StyledAlertProps = {
  $isOneLinerMessage: boolean;
  $isPositionTop: boolean;
  $customMarginTop: string;
};

const StyledAlert = styled.div<StyledAlertProps>`
  border: 1px solid #e33f3f;
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 12px 15px;
  margin-top: ${({ $customMarginTop }) => $customMarginTop};
  position: relative;
  line-height: 1.3;
  text-align: left;
  width: ${({ $isOneLinerMessage }) => $isOneLinerMessage && 'max-content'};
  // 吹き出し
  :before,
  :after {
    content: '';
    border: solid transparent;
    pointer-events: none;
    position: absolute;
    bottom: 100%;
    left: 50%;
    ${(props) =>
      props.$isPositionTop &&
      css`
        transform: rotate(180deg);
        bottom: -28px;
      `}
  }
  :before {
    border: 7px solid transparent;
    border-bottom: 13px solid ${PALETTE.error.main};
    margin-left: -7px;
    margin-bottom: 0;
    bottom: ${({ $isPositionTop }) => $isPositionTop && '-20px'};
  }
  :after {
    border: 6px solid transparent;
    border-bottom: 11px solid ${PALETTE.error.light};
    margin-left: -6px;
    bottom: ${({ $isPositionTop }) => $isPositionTop && '-16px'};
  }
  .inquireButton {
    margin: 10px 35px 0 35px;
    border: 1px solid ${PALETTE.error.main};
    background-color: ${PALETTE.bg.main};
    color: ${PALETTE.error.main};
    padding: 10px 25px;
    text-align: center;
    text-decoration: none;
    display: block;
    font-size: ${FONT_SIZES.body3};
    border-radius: ${BUTTON.squareRoundBorder.radius};
    pointer-events: auto;
  }
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  svg {
    font-size: 18px;
    margin-right: 2px;
  }
`;

type InquiryButtonProps = {
  href: string;
};

const InquiryButton: React.FC<InquiryButtonProps> = ({ href }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <span
      data-testid="uiParts-InputValidationErrorTooltip-btn"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: `<a class="inquireButton" href='${href}' target='_blank' rel='noopener noreferrer'>${t(
          'appCommon.inquire'
        )}</a>`,
      }}
    />
  );
};

type Props = {
  children: JSX.Element;
  error: boolean;
  message: string;
  zIndex?: number;
  placement?: TooltipProps['placement'];
  dataTestId?: string;
  inquiryLink?: string;
  disablePortal?: boolean;
  isOneLinerMessage?: boolean;
  customMarginTop?: string;
};

const InputValidationErrorTooltip: React.FC<Props> = ({
  children,
  error,
  message,
  zIndex = Z_INDEX.tooltip,
  placement = 'bottom',
  dataTestId = null,
  inquiryLink = null,
  disablePortal = false, // 配置箇所のDOM構造のまま表示するかどうか
  isOneLinerMessage = false, // エラーメッセージを一行表示するか
  customMarginTop = '-2px',
}): JSX.Element => (
  <Wrapper
    data-testid={
      /* istanbul ignore next */
      dataTestId
        ? `uiParts-InputValidationErrorTooltip-${dataTestId}`
        : `uiParts-InputValidationErrorTooltip`
    }
  >
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
          },
        },
      }}
      PopperProps={
        {
          sx: {
            zIndex,
          },
          disablePortal,
          modifiers: [
            // スクロールによってツールチップの上下移動移動を許可しない
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: false,
            },
          ],
          /* istanbul ignore next */
          'data-testid': dataTestId
            ? `uiParts-InputValidationErrorPopper-${dataTestId}`
            : `uiParts-InputValidationErrorPopper`,
        } as PopperPropsWithDataTestId
      }
      open={error}
      placement={placement}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 10 }}
      arrow={false}
      disableInteractive
      title={
        <StyledAlert
          $customMarginTop={customMarginTop}
          $isOneLinerMessage={isOneLinerMessage}
          $isPositionTop={['top', 'top-start', 'top-end'].includes(placement)}
        >
          <StyledTypography variant="body3">
            <WarningRoundedIcon />
            <span
              /* istanbul ignore next */
              data-testid={
                dataTestId
                  ? `uiParts-InputValidationErrorTooltip-errorMessage-${dataTestId}`
                  : `uiParts-InputValidationErrorTooltip-errorMessage`
              }
            >
              {message}
            </span>
          </StyledTypography>
          {inquiryLink ? <InquiryButton href={inquiryLink} /> : null}
        </StyledAlert>
      }
    >
      {children}
    </Tooltip>
  </Wrapper>
);

export default InputValidationErrorTooltip;
